/**
 * FleetPolicyWorkspace — versioned importer discount programs (fleet policy).
 *
 * Flow (mirrors EbmWorkspace's upload → review → version pattern):
 *   1. The admin uploads the quarterly PDF (`POST /v1/fleet-policy/extract`).
 *      The PDF is always stored to S3; when the server has an Anthropic API
 *      key, the response also carries an AI-extracted draft.
 *   2. The draft renders as an editable review table (header + rows). Nothing
 *      is live until the admin saves — saving creates the next append-only
 *      version for the selected program type. Manual entry (no PDF / no AI)
 *      uses the same editor via "Nová verze ručně".
 *   3. The active (or any past) version renders below, switchable via the
 *      version dropdown; the source PDF stays downloadable per version.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { useConfirm } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { toast } from '@/components/Toaster';
import { formatInt, formatMoney } from '@/lib/format';
import {
  FLEET_POLICY_TYP_LABEL as PROGRAM_TYP_LABEL,
  openFleetPolicyZdroj,
  useFleetPolicyCreateMutation,
  useFleetPolicyExtractMutation,
  useFleetPolicyQuery,
  type FleetPolicyExtractProgress,
  type FleetPolicyProgramTyp,
  type FleetPolicyRadek,
  type FleetPolicyRadekInput,
} from '../api';

interface FleetPolicyWorkspaceProps {
  isAdmin: boolean;
}

const TYPY: FleetPolicyProgramTyp[] = ['fleet', 'retail_ov', 'retail_uv'];

/** Editable row — numbers kept as strings while typing. */
interface EditRow {
  skupina: string;
  model_nazev: string;
  model_kod: string;
  podminka: string;
  mr_od: string;
  mr_do: string;
  pohon: string;
  varianta: string;
  vyloucene: string;
  pasmo_a: string;
  pasmo_b: string;
  pasmo_c: string;
  procento: string;
  kc_zvyhodneni: string;
  kc_fmc: string;
  kc_ap: string;
  poznamka: string;
}

interface DraftState {
  nazev: string;
  platnost_od: string;
  platnost_do: string;
  poznamka: string;
  zdrojKlic: string | null;
  zdrojNazev: string | null;
  rows: EditRow[];
}

const EMPTY_ROW: EditRow = {
  skupina: '', model_nazev: '', model_kod: '', podminka: '', mr_od: '', mr_do: '',
  pohon: '', varianta: '', vyloucene: '', pasmo_a: '', pasmo_b: '', pasmo_c: '',
  procento: '', kc_zvyhodneni: '', kc_fmc: '', kc_ap: '', poznamka: '',
};

const numToStr = (v: number | null) => (v == null ? '' : String(v));
const strToNum = (s: string): number | null => {
  const t = s.trim().replace(',', '.').replace(/\s/g, '');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

function inputRowToEdit(r: FleetPolicyRadekInput): EditRow {
  return {
    skupina: r.skupina ?? '',
    model_nazev: r.model_nazev,
    model_kod: r.model_kod ?? '',
    podminka: r.podminka ?? '',
    mr_od: numToStr(r.mr_od),
    mr_do: numToStr(r.mr_do),
    pohon: r.pohon ?? '',
    varianta: r.varianta ?? '',
    vyloucene: r.vyloucene ?? '',
    pasmo_a: numToStr(r.pasmo_a),
    pasmo_b: numToStr(r.pasmo_b),
    pasmo_c: numToStr(r.pasmo_c),
    procento: numToStr(r.procento),
    kc_zvyhodneni: numToStr(r.kc_zvyhodneni),
    kc_fmc: numToStr(r.kc_fmc),
    kc_ap: numToStr(r.kc_ap),
    poznamka: r.poznamka ?? '',
  };
}

function editRowToInput(r: EditRow): FleetPolicyRadekInput {
  const str = (s: string) => (s.trim() === '' ? null : s.trim());
  return {
    skupina: str(r.skupina),
    model_nazev: r.model_nazev.trim(),
    model_kod: str(r.model_kod),
    vozidlo_model: null,
    podminka: str(r.podminka),
    mr_od: strToNum(r.mr_od),
    mr_do: strToNum(r.mr_do),
    pohon: str(r.pohon),
    varianta: str(r.varianta),
    vyloucene: str(r.vyloucene),
    pasmo_a: strToNum(r.pasmo_a),
    pasmo_b: strToNum(r.pasmo_b),
    pasmo_c: strToNum(r.pasmo_c),
    procento: strToNum(r.procento),
    kc_zvyhodneni: strToNum(r.kc_zvyhodneni),
    kc_fmc: strToNum(r.kc_fmc),
    kc_ap: strToNum(r.kc_ap),
    poznamka: str(r.poznamka),
  };
}

type ValueColumn = { key: keyof EditRow & keyof FleetPolicyRadek; label: string; unit: '%' | 'Kč' };

const ALL_VALUE_COLUMNS: ValueColumn[] = [
  { key: 'pasmo_a', label: 'Pásmo A', unit: '%' },
  { key: 'pasmo_b', label: 'Pásmo B', unit: '%' },
  { key: 'pasmo_c', label: 'Pásmo C', unit: '%' },
  { key: 'procento', label: 'Podpora', unit: '%' },
  { key: 'kc_zvyhodneni', label: 'Zvýhodnění', unit: 'Kč' },
  { key: 'kc_fmc', label: 'FMC', unit: 'Kč' },
  { key: 'kc_ap', label: 'AP', unit: 'Kč' },
];

/** Default value columns of a program type (always shown). */
function valueColumns(typ: FleetPolicyProgramTyp): ValueColumn[] {
  if (typ === 'fleet') return ALL_VALUE_COLUMNS.slice(0, 3);
  return ALL_VALUE_COLUMNS.slice(4);
}

/**
 * Type defaults plus any column that actually carries data. A fleet PDF also
 * brings "Ostatní programy" Kč bonuses and JSDH percentages — those must stay
 * visible (and editable in review) even though they aren't pásma.
 */
function activeColumns(typ: FleetPolicyProgramTyp, hasValue: (key: ValueColumn['key']) => boolean): ValueColumn[] {
  const base = valueColumns(typ);
  const present = new Set(base.map((c) => c.key));
  for (const c of ALL_VALUE_COLUMNS) {
    if (!present.has(c.key) && hasValue(c.key)) base.push(c);
  }
  return base;
}

function formatValue(v: number | null, unit: '%' | 'Kč'): string {
  if (v == null) return '—';
  return unit === '%' ? `${formatInt(v)} %` : `${formatMoney(v)} Kč`;
}

const cellInput =
  'w-full border border-line bg-paper px-1.5 py-1 text-[11.5px] text-ink focus:border-accent focus:outline-none';

export function FleetPolicyWorkspace({ isAdmin }: FleetPolicyWorkspaceProps) {
  const { confirm, dialog: confirmDialog } = useConfirm();
  const [typ, setTyp] = useState<FleetPolicyProgramTyp>('fleet');
  const [viewVerze, setViewVerze] = useState<number | undefined>(undefined);
  const [file, setFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [aiNote, setAiNote] = useState<string | null>(null);

  // Live extraction progress (the endpoint streams NDJSON events) + a ticking
  // elapsed counter so the minutes-long AI run never looks stuck.
  const [extractStav, setExtractStav] = useState<
    { faze: FleetPolicyExtractProgress['faze']; pocetRadku: number; od: number } | null
  >(null);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!extractStav) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [extractStav]);

  const policy = useFleetPolicyQuery(typ, viewVerze, isAdmin);
  const extract = useFleetPolicyExtractMutation();
  const create = useFleetPolicyCreateMutation();

  const versions = policy.data?.versions ?? [];
  const shown = policy.data?.version ?? null;
  const rows = policy.data?.rows ?? [];
  const aktivniId = policy.data?.aktivniId ?? null;

  const tableCols = useMemo(
    () => activeColumns(typ, (k) => rows.some((r) => r[k] != null)),
    [typ, rows],
  );
  const draftCols = useMemo(
    () =>
      draft
        ? activeColumns(typ, (k) => draft.rows.some((r) => r[k].trim() !== ''))
        : valueColumns(typ),
    [typ, draft],
  );

  const switchTyp = (next: FleetPolicyProgramTyp) => {
    setTyp(next);
    setViewVerze(undefined);
  };

  const runExtract = () => {
    if (!file) {
      toast.warn('Nejprve vyberte PDF vyhlášení programu.');
      return;
    }
    setExtractStav({ faze: 'ulozeno', pocetRadku: 0, od: Date.now() });
    extract.mutate(
      {
        file,
        programTyp: typ,
        onProgress: (p) =>
          setExtractStav((prev) => ({ ...p, od: prev?.od ?? Date.now() })),
      },
      {
        onSettled: () => setExtractStav(null),
        onSuccess: (r) => {
          setFile(null);
          if (fileInput.current) fileInput.current.value = '';
          if (r.draft) {
            if (r.draft.program_typ !== typ) {
              setTyp(r.draft.program_typ);
              setViewVerze(undefined);
              toast.warn(
                `AI rozpoznala typ programu „${PROGRAM_TYP_LABEL[r.draft.program_typ]}" — přepnuto.`,
              );
            }
            setDraft({
              nazev: r.draft.nazev,
              platnost_od: r.draft.platnost_od ?? '',
              platnost_do: r.draft.platnost_do ?? '',
              poznamka: r.draft.poznamka ?? '',
              zdrojKlic: r.zdrojKlic,
              zdrojNazev: r.zdrojNazev,
              rows: r.draft.radky.map(inputRowToEdit),
            });
            setAiNote(null);
            toast.ok(`PDF vytěženo — ${formatInt(r.draft.radky.length)} řádků ke kontrole.`);
          } else {
            setDraft({
              nazev: '', platnost_od: '', platnost_do: '', poznamka: '',
              zdrojKlic: r.zdrojKlic, zdrojNazev: r.zdrojNazev, rows: [{ ...EMPTY_ROW }],
            });
            setAiNote(
              r.chyba ?? 'AI extrakce není nakonfigurována (ANTHROPIC_API_KEY) — zadejte řádky ručně.',
            );
            toast.warn(r.chyba ?? 'PDF uloženo, ale bez AI extrakce — zadejte řádky ručně.');
          }
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Nahrání PDF selhalo'),
      },
    );
  };

  const startManual = () => {
    setDraft({
      nazev: '', platnost_od: '', platnost_do: '', poznamka: '',
      zdrojKlic: null, zdrojNazev: null, rows: [{ ...EMPTY_ROW }],
    });
    setAiNote(null);
  };

  const discardDraft = async () => {
    if (draft && draft.rows.some((r) => r.model_nazev.trim() !== '')) {
      const ok = await confirm({
        title: 'Zahodit rozpracovanou verzi?',
        description: 'Rozpracované řádky se neuloží. Zdrojové PDF zůstane uloženo.',
        confirmLabel: 'Zahodit',
      });
      if (!ok) return;
    }
    setDraft(null);
    setAiNote(null);
  };

  const setRow = (i: number, patch: Partial<EditRow>) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = [...prev.rows];
      next[i] = { ...next[i], ...patch };
      return { ...prev, rows: next };
    });
  };

  const removeRow = (i: number) =>
    setDraft((prev) => (prev ? { ...prev, rows: prev.rows.filter((_, j) => j !== i) } : prev));

  const addRow = () => setDraft((prev) => (prev ? { ...prev, rows: [...prev.rows, { ...EMPTY_ROW }] } : prev));

  const saveDraft = () => {
    if (!draft) return;
    const valid = draft.rows.filter((r) => r.model_nazev.trim() !== '');
    if (draft.nazev.trim() === '') {
      toast.warn('Vyplňte název verze (např. „Fleet Policy Q3/2026").');
      return;
    }
    if (valid.length === 0) {
      toast.warn('Verze musí obsahovat alespoň jeden řádek s modelem.');
      return;
    }
    create.mutate(
      {
        program_typ: typ,
        nazev: draft.nazev.trim(),
        platnost_od: draft.platnost_od.trim() || null,
        platnost_do: draft.platnost_do.trim() || null,
        poznamka: draft.poznamka.trim() || null,
        zdroj_klic: draft.zdrojKlic,
        zdroj_nazev: draft.zdrojNazev,
        rows: valid.map(editRowToInput),
      },
      {
        onSuccess: (res) => {
          toast.ok(`Vytvořena verze ${res.verze} — ${formatInt(res.pocet)} řádků.`);
          setDraft(null);
          setAiNote(null);
          setViewVerze(undefined);
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Uložení verze selhalo'),
      },
    );
  };

  const openZdroj = (verzeId: number, nazev: string | null) =>
    openFleetPolicyZdroj(verzeId, nazev).catch((err) =>
      toast.error(err instanceof Error ? err.message : 'Stažení PDF selhalo'),
    );

  if (!isAdmin) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5">
        <EmptyState
          eyebrow="403"
          title="Fleet policy vyžaduje administrátorský přístup k modulu."
          description="Pro přístup požádejte správce firmy."
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {confirmDialog}
      <Toolband>
        <label className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Program
          <select
            value={typ}
            onChange={(e) => switchTyp(e.target.value as FleetPolicyProgramTyp)}
            disabled={draft != null}
            className="border border-line bg-paper px-1.5 py-1 font-mono text-[11px] text-ink focus:border-accent focus:outline-none disabled:opacity-50"
          >
            {TYPY.map((t) => (
              <option key={t} value={t}>
                {PROGRAM_TYP_LABEL[t]}
              </option>
            ))}
          </select>
        </label>

        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Button
          variant="secondary"
          size="sm"
          icon={<Icon name="inbox" size={12} />}
          onClick={() => fileInput.current?.click()}
          disabled={draft != null}
        >
          {file ? 'Změnit PDF' : 'Vybrat PDF'}
        </Button>
        {file && (
          <span className="max-w-[200px] truncate font-mono text-[10.5px] text-ink-2" title={file.name}>
            {file.name}
          </span>
        )}
        <Button
          variant="ink"
          size="sm"
          icon={<Icon name="upload" size={12} />}
          onClick={runExtract}
          disabled={!file || extract.isPending || draft != null}
        >
          {extract.isPending ? 'Vytěžuji…' : 'Nahrát a vytěžit'}
        </Button>
        <Button variant="secondary" size="sm" onClick={startManual} disabled={draft != null}>
          Nová verze ručně
        </Button>

        <Toolband.Spacer />

        {versions.length > 0 && (
          <label className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
            Verze
            <select
              value={viewVerze ?? ''}
              onChange={(e) => setViewVerze(e.target.value === '' ? undefined : Number(e.target.value))}
              className="border border-line bg-paper px-1.5 py-1 font-mono text-[11px] text-ink focus:border-accent focus:outline-none"
            >
              <option value="">Aktuální</option>
              {versions.map((v) => (
                <option key={v.id} value={v.verze}>
                  v{v.verze} · {v.nazev} · {v.pocet} řádků
                </option>
              ))}
            </select>
          </label>
        )}
      </Toolband>

      <div
        data-testid="fleet-policy-workspace"
        className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto px-8 pb-8 pt-5"
      >
        {extractStav && (
          <div
            data-testid="fleet-policy-extract-progress"
            className="flex items-center gap-3 border border-line bg-paper px-4 py-3"
          >
            <span className="inline-block h-2 w-2 animate-pulse bg-accent" aria-hidden />
            <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-2">
              {extractStav.faze === 'ulozeno' && 'Nahrávám PDF a předávám AI…'}
              {extractStav.faze === 'cteni' && 'AI čte dokument a rozmýšlí…'}
              {extractStav.faze === 'radky' &&
                `AI vytěžuje řádky — zatím ${formatInt(extractStav.pocetRadku)}`}
            </span>
            <span className="ml-auto font-mono text-[11px] tabular-nums text-ink-3">
              {(() => {
                const s = Math.floor((Date.now() - extractStav.od) / 1000);
                return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
              })()}
            </span>
          </div>
        )}
        {draft && (
          <section data-testid="fleet-policy-draft" className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-2">
                  Kontrola před uložením — {PROGRAM_TYP_LABEL[typ]}
                </h3>
                <p className="mt-0.5 text-[12px] text-ink-3">
                  Zkontrolujte a upravte vytěžené řádky. Uložením vznikne nová verze; starší verze
                  zůstávají v historii.
                  {draft.zdrojNazev ? ` Zdroj: ${draft.zdrojNazev}` : ''}
                </p>
                {aiNote && <p className="mt-1 text-[12px] text-warn">{aiNote}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="secondary" size="sm" onClick={discardDraft} disabled={create.isPending}>
                  Zahodit
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<Icon name="check" size={12} />}
                  onClick={saveDraft}
                  disabled={create.isPending}
                >
                  {create.isPending ? 'Ukládám…' : 'Uložit jako novou verzi'}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="flex flex-col gap-1 text-[11px] text-ink-3">
                Název verze
                <input
                  value={draft.nazev}
                  onChange={(e) => setDraft((p) => (p ? { ...p, nazev: e.target.value } : p))}
                  placeholder="Fleet Policy Q3/2026"
                  className={cellInput}
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] text-ink-3">
                Platnost od
                <input
                  type="date"
                  value={draft.platnost_od}
                  onChange={(e) => setDraft((p) => (p ? { ...p, platnost_od: e.target.value } : p))}
                  className={cellInput}
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] text-ink-3">
                Platnost do
                <input
                  type="date"
                  value={draft.platnost_do}
                  onChange={(e) => setDraft((p) => (p ? { ...p, platnost_do: e.target.value } : p))}
                  className={cellInput}
                />
              </label>
            </div>
            <label className="flex flex-col gap-1 text-[11px] text-ink-3">
              Poznámka / pravidla programu (pásma, zvýhodněné skupiny, vyloučení…)
              <textarea
                value={draft.poznamka}
                onChange={(e) => setDraft((p) => (p ? { ...p, poznamka: e.target.value } : p))}
                rows={4}
                className={`${cellInput} resize-y`}
              />
            </label>

            <div className="overflow-x-auto border border-line bg-paper">
              <table className="w-full border-collapse text-[11.5px]">
                <thead className="bg-bg">
                  <tr className="text-left font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                    <th className="min-w-[120px] px-2 py-2">Model</th>
                    <th className="min-w-[90px] px-2 py-2">Kód</th>
                    <th className="min-w-[80px] px-2 py-2">Skupina</th>
                    <th className="min-w-[140px] px-2 py-2">Podmínka</th>
                    <th className="w-[76px] px-2 py-2">MR od</th>
                    <th className="w-[76px] px-2 py-2">MR do</th>
                    <th className="w-[80px] px-2 py-2">Pohon</th>
                    <th className="min-w-[120px] px-2 py-2">Varianta</th>
                    <th className="min-w-[100px] px-2 py-2">Mimo</th>
                    {draftCols.map((c) => (
                      <th key={c.key} className="w-[90px] px-2 py-2 text-right">
                        {c.label} ({c.unit})
                      </th>
                    ))}
                    <th className="min-w-[110px] px-2 py-2">Poznámka</th>
                    <th className="w-[40px] px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {draft.rows.map((r, i) => (
                    <tr key={i} data-testid="fleet-policy-draft-row" className="border-t border-line align-top">
                      <td className="px-2 py-1.5">
                        <input value={r.model_nazev} onChange={(e) => setRow(i, { model_nazev: e.target.value })} aria-label={`Model řádku ${i + 1}`} className={cellInput} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={r.model_kod} onChange={(e) => setRow(i, { model_kod: e.target.value })} className={cellInput} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={r.skupina} onChange={(e) => setRow(i, { skupina: e.target.value })} className={cellInput} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={r.podminka} onChange={(e) => setRow(i, { podminka: e.target.value })} className={cellInput} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={r.mr_od} onChange={(e) => setRow(i, { mr_od: e.target.value })} placeholder="2026.5" className={`${cellInput} tabular-nums`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={r.mr_do} onChange={(e) => setRow(i, { mr_do: e.target.value })} className={`${cellInput} tabular-nums`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={r.pohon} onChange={(e) => setRow(i, { pohon: e.target.value })} placeholder="BEV" className={cellInput} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={r.varianta} onChange={(e) => setRow(i, { varianta: e.target.value })} className={cellInput} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={r.vyloucene} onChange={(e) => setRow(i, { vyloucene: e.target.value })} className={cellInput} />
                      </td>
                      {draftCols.map((c) => (
                        <td key={c.key} className="px-2 py-1.5">
                          <input
                            value={r[c.key]}
                            onChange={(e) => setRow(i, { [c.key]: e.target.value } as Partial<EditRow>)}
                            aria-label={`${c.label} řádku ${i + 1}`}
                            className={`${cellInput} text-right tabular-nums`}
                          />
                        </td>
                      ))}
                      <td className="px-2 py-1.5">
                        <input value={r.poznamka} onChange={(e) => setRow(i, { poznamka: e.target.value })} className={cellInput} />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          type="button"
                          onClick={() => removeRow(i)}
                          aria-label={`Odebrat řádek ${i + 1}`}
                          className="text-ink-3 hover:text-danger"
                        >
                          <Icon name="close" size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <Button variant="secondary" size="sm" icon={<Icon name="plus" size={12} />} onClick={addRow}>
                Přidat řádek
              </Button>
            </div>
          </section>
        )}

        <section className="flex min-h-0 flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-2">
              {PROGRAM_TYP_LABEL[typ]}
              {shown && (
                <span className="ml-2 normal-case tracking-normal text-ink-3">
                  v{shown.verze} · {shown.nazev} · {formatInt(shown.pocet)} řádků
                  {shown.platnost_od ? ` · ${shown.platnost_od} → ${shown.platnost_do ?? '…'}` : ''}
                  {aktivniId != null && shown.id === aktivniId ? ' · platná' : ' · neplatná / historická'}
                </span>
              )}
            </h3>
            {shown?.zdroj_klic && (
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon name="download" size={12} />}
                onClick={() => openZdroj(shown.id, shown.zdroj_nazev)}
              >
                Zdrojové PDF
              </Button>
            )}
          </div>

          {policy.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : policy.isError ? (
            <EmptyState
              eyebrow="Chyba"
              title="Načtení fleet policy selhalo."
              description={policy.error instanceof Error ? policy.error.message : 'Zkuste to znovu.'}
            />
          ) : versions.length === 0 ? (
            <EmptyState
              eyebrow="Zatím prázdné"
              title={`Žádná verze programu ${PROGRAM_TYP_LABEL[typ]}.`}
              description="Nahrajte PDF vyhlášení programu, nebo založte verzi ručně."
            />
          ) : (
            <>
              {shown?.poznamka && (
                <p className="whitespace-pre-wrap border border-line bg-paper px-4 py-3 text-[12px] leading-relaxed text-ink-2">
                  {shown.poznamka}
                </p>
              )}
              <div className="overflow-x-auto border border-line bg-paper">
                <table className="w-full border-collapse text-[12px]">
                  <thead className="bg-bg">
                    <tr className="text-left font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                      <th className="px-3 py-2.5">Model</th>
                      <th className="px-3 py-2.5">Podmínka</th>
                      <th className="px-3 py-2.5">MR</th>
                      <th className="px-3 py-2.5">Pohon</th>
                      <th className="px-3 py-2.5">Varianta / mimo</th>
                      {tableCols.map((c) => (
                        <th key={c.key} className="px-3 py-2.5 text-right">
                          {c.label}
                        </th>
                      ))}
                      <th className="px-3 py-2.5">Poznámka</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} data-testid="fleet-policy-row" className="border-t border-line align-top">
                        <td className="px-3 py-2">
                          <span className="font-semibold text-ink">{r.model_nazev}</span>
                          {r.model_kod && (
                            <span className="ml-1.5 font-mono text-[10.5px] text-ink-3">{r.model_kod}</span>
                          )}
                          {r.skupina && <div className="text-[10.5px] text-ink-3">{r.skupina}</div>}
                        </td>
                        <td className="px-3 py-2 text-ink-2">{r.podminka ?? '—'}</td>
                        <td className="px-3 py-2 font-mono text-[11.5px] tabular-nums text-ink-2">
                          {r.mr_od == null && r.mr_do == null
                            ? '—'
                            : `${r.mr_od ?? '…'} – ${r.mr_do ?? '…'}`}
                        </td>
                        <td className="px-3 py-2 text-ink-2">{r.pohon ?? '—'}</td>
                        <td className="px-3 py-2 text-ink-2">
                          {r.varianta ?? ''}
                          {r.vyloucene && <span className="text-ink-3"> mimo: {r.vyloucene}</span>}
                          {!r.varianta && !r.vyloucene && '—'}
                        </td>
                        {tableCols.map((c) => (
                          <td key={c.key} className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                            {formatValue(r[c.key] as number | null, c.unit)}
                          </td>
                        ))}
                        <td className="px-3 py-2 text-[11.5px] text-ink-3">{r.poznamka ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
